import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Constant } from "app/constants/constant";
import { ResponseResultCode } from "app/model/response-api";
import ArcButton, { ButtonIcon, ButtonType } from "app/components/arc/ArcButton";
import { useNavigate } from "react-router-dom";
import { numberFormatter, parseNumberWithCommaDecimal } from "app/utils/stringUtil";
import Highcharts from "highcharts";
import { HighchartsOptionsUtil } from "app/utils/highchartsOptionsUtil";
import HighchartsReact from "highcharts-react-official";
import { ChartUtil } from "app/utils/chartUtil";
import { DbService } from "app/services/db/dbService";
import DashboardLoading from "app/pages/db/modules/DashboardLoading";
import useInterval from "app/hooks/use-interval";

const defaultOption: Highcharts.Options = {
  chart: {
    type: 'column',
    height: 125,
    spacing: [0, 0, 0, 0],
    margin: [25, 10, 20, 30],
  },
  plotOptions: {
    column: {
      borderWidth: 0,
    },
  },
  tooltip: {
    formatter: function(this:any):any {
      let s = `<div><b>${this.x}</b>`;
      this.points.forEach((item: any) => {
        s += '<br />';
        s += `<span style="color:${item.color};">●</span>&nbsp;`;
        s += `<span style='font-weight:500;'>전력 사용량:&nbsp;</span>`;
        s += `<span style='font-weight:700;'>${numberFormatter(item.y, 2)}</span> MWh`;
      });
      s += `</div>`;
      return s;
    },
  },
  legend: { enabled: false },
  xAxis: {
    categories: [...ChartUtil.getMonthForCategories()],
    min: 0,
    crosshair: true,
    lineColor: '#E1E1E1',
    title: { text: null },
    labels: {
      y: 15,
      step: 5,
      style: {
        color: '#444',
        fontSize: '0.6rem',
      },
    },
  },
  yAxis: {
    title: {
      text: "(MWh)",
      align: 'high',
      rotation: 0,
      offset: 0,
      x: 10,
      y: -10,
      style: {
        color: '#444',
        fontSize: '0.6rem',
      },
    },
    min: 0,
    softMax: 10,
    lineColor: "#999999",
    labels: {
      x: -5,
      y: 4,
      style: {
        color: '#444',
        fontSize: '0.6rem',
      },
      formatter: function (this: any) {
        return parseNumberWithCommaDecimal(this.value);
      },
    },
  },
  series: [],
};

interface BasicData {
  site: number;
  pwr: number;
  ghgs: number;
}

export default function DbEnrgStatus() {
  Highcharts.setOptions({
    ...HighchartsOptionsUtil.locale.kr.globalOptions,
    title: { text: '' },
    exporting: {enabled: false},
    credits: {enabled: false},
    tooltip: {shared: true, headerFormat: ''},
    accessibility: {enabled: false},
  });

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [option, setOption] = useState<Highcharts.Options>(defaultOption);
  const initBasic: BasicData = { site: 0, pwr: 0, ghgs: 0 };
  const [basic, setBasic] = useState<BasicData>(initBasic);

  useEffect(() => {
    Promise.allSettled([fetchTrend(), fetchAgg()])
    .finally(() => {
      setIsLoading(false);
    });
  }, []);

  useInterval(() => {
    Promise.allSettled([fetchTrend(), fetchAgg()])
    .finally(() => {
      setIsLoading(false);
    });
  }, 600000);

  async function fetchTrend() {
    const fromDt: string = dayjs().format(Constant.FORMAT_YMDHMS);
    DbService.getInstance().getEnrgMonthTrend(fromDt)
    .then(response => {
      if (response && ResponseResultCode.Y === response.resultCode) {
        setOption({
          ...defaultOption,
          xAxis: {
            ...defaultOption.xAxis,
            categories: response.data?.map(i => `${i.day}일`),
          },
          series: [{ type: 'column', data: response.data?.map(i => (i.pwrUse ?? 0) / 1000)}],
        })
      } else {
        setOption(defaultOption);
      }
    })
    .catch(() => {
      setOption(defaultOption);
    });
  }

  async function fetchAgg() {
    try{
      const fromDt: string = dayjs().format(Constant.FORMAT_YMDHMS);
      const coResponse = await DbService.getInstance().getEnrgMonthAgg(fromDt);
      const cpResponse = await DbService.getInstance().getEnrgMonthCarbon();
      let ghgs = 0;
      let site = 0;
      let pwr = 0;

      if (cpResponse && cpResponse.resultCode === "Y") {
        if(cpResponse?.data) {
          ghgs = cpResponse.data?.thisMonthQnt;
        }
      }

      if(coResponse && coResponse.resultCode === "Y") {
        if(coResponse?.data) {
          site = coResponse?.data.cnt;
          pwr = coResponse.data?.pwrUse/1000;
        }
      }
      setBasic({
        site,
        pwr,
        ghgs
      })
    } catch(e) {
      setBasic(initBasic)
    }
  }

  return (
    <div className="cont">
      <DashboardLoading isLoading={isLoading} />
      <h2 className="titT2 bl">
        <span>산단 월간 에너지 현황</span>
      </h2>
      <div className="chart">
        {option && (
          <HighchartsReact highcharts={Highcharts} options={option} />
        )}
      </div>
      <div className="txt-flex">
        <div>
          <span>월 전력 사용량</span>
          <strong><em>{numberFormatter(basic.pwr, 2)}</em>MWh</strong>
        </div>
        <div>
          <span>월 탄소배출량</span>
          <strong><em>{numberFormatter(basic.ghgs, 2)}</em>tCO₂eq</strong>
        </div>
      </div>
      <ArcButton
        type={ButtonType.custom}
        className="more-btn"
        icon={ButtonIcon.plus}
        hasIcon
        onClick={() => navigate('/ic/ele/amountusecomplex', {state:true})}
      />
    </div>
  );
}